import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import toast from 'react-hot-toast';
import {
    Button,
    TextField,
    TablePagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { BsDownload } from 'react-icons/bs';
import * as XLSX from "xlsx";
import PageHeader1 from '../../components/common/PageHeader1';
import { fetchUsers } from '../../Redux/Reducers/UserManagementReducer';
import { formatUTCDate, convertISTToUTCForAPI } from '../../utils/dateUtils';
import { useSearchParams } from 'react-router-dom';
import { getIntParam, getStringParam, setIfTruthy } from '../../utils/urlState';

function UsersList() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const usersFromStore = useSelector((state) => state?.UserManagementReducer?.users || []);
    const paginationInfo = useSelector((state) => state?.UserManagementReducer?.pagination);
    const loading = useSelector((state) => state?.UserManagementReducer?.loading || false);
    const didInitUrlSyncRef = useRef(false);
    const applySawLoadingRef = useRef(false);
    const [isApplyingFilters, setIsApplyingFilters] = useState(false);

    // Filter States
    const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(getStringParam(searchParams, "search", ""));
    const [searchType, setSearchType] = useState(getStringParam(searchParams, "searchType", "fullName"));
    const [appliedSearchType, setAppliedSearchType] = useState(getStringParam(searchParams, "searchType", "fullName"));

    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const todayDate = getTodayDate();
    const [startDate, setStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [endDate, setEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [appliedStartDate, setAppliedStartDate] = useState(getStringParam(searchParams, "startDate", ""));
    const [appliedEndDate, setAppliedEndDate] = useState(getStringParam(searchParams, "endDate", ""));
    const [lastAction, setLastAction] = useState(null);

    const [paginationModel, setPaginationModel] = useState({
        page: getIntParam(searchParams, "page", 0),
        pageSize: getIntParam(searchParams, "pageSize", 10),
    });
    const [isExporting, setIsExporting] = useState(false);
    const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

    const isInitialMount = useRef(true);
    const lastParamsRef = useRef(null);

    // Apply Filters Handler
    const handleApplyFilters = () => {
        setIsApplyingFilters(true);
        applySawLoadingRef.current = false;
        setAppliedSearchQuery(searchQuery.trim());
        setAppliedSearchType(searchType);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setLastAction("apply");
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    // Reset Filter Handler
    const handleResetFilter = () => {
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchQuery("");
        setAppliedSearchQuery("");
        setSearchType('fullName');
        setAppliedSearchType('fullName');
        setLastAction(null);
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        const params = {
            page: page + 1,
            limit: pageSize,
        };

        if (appliedStartDate && appliedEndDate) {
            params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
        } else if (lastAction === null) {
            params.startDate = convertISTToUTCForAPI(todayDate, 'start');
            params.endDate = convertISTToUTCForAPI(todayDate, 'end');
        }

        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            params[appliedSearchType] = appliedSearchQuery;
        }

        const paramsString = JSON.stringify(params);
        if (lastParamsRef.current === paramsString && !isInitialMount.current) return;

        lastParamsRef.current = paramsString;
        isInitialMount.current = false;

        dispatch(fetchUsers(params));
    }, [paginationModel, appliedSearchQuery, appliedStartDate, appliedEndDate, appliedSearchType, lastAction, todayDate, dispatch]);

    // Apply button should stay disabled until request completes.
    useEffect(() => {
        if (!isApplyingFilters) return;
        if (loading) {
            applySawLoadingRef.current = true;
            return;
        }
        if (!loading && applySawLoadingRef.current) {
            applySawLoadingRef.current = false;
            setIsApplyingFilters(false);
        }
    }, [loading, isApplyingFilters]);

    // Keep URL in sync so Back restores pagination/filters.
    useEffect(() => {
        if (!didInitUrlSyncRef.current) {
            didInitUrlSyncRef.current = true;
            return;
        }

        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page));
        params.set("pageSize", String(paginationModel.pageSize));
        if (appliedSearchQuery && appliedSearchQuery.trim()) {
            params.set("searchType", appliedSearchType);
            params.set("search", appliedSearchQuery.trim());
        }
        setIfTruthy(params, "startDate", appliedStartDate);
        setIfTruthy(params, "endDate", appliedEndDate);
        setSearchParams(params, { replace: true });
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        appliedSearchQuery,
        appliedSearchType,
        appliedStartDate,
        appliedEndDate,
        setSearchParams,
    ]);

    const handleChangePage = (event, newPage) => {
        setPaginationModel({ ...paginationModel, page: newPage });
    };

    const handleChangeRowsPerPage = (event) => {
        setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
    };

    const columns = [
        { field: 'sono', headerName: 'S.No', width: 70 },
        { field: 'fullName', headerName: 'Name', width: 200 },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'mobileNumber', headerName: 'Mobile', width: 150 },
        { field: 'createdAt', headerName: 'Created At', width: 180 },
        { field: 'updatedAt', headerName: 'Updated At', width: 180 },
    ];

    const currentGridRows = usersFromStore.map((user, index) => ({
        id: user._id,
        sono: (paginationModel.page * paginationModel.pageSize) + index + 1,
        fullName: user.fullName || 'N/A',
        email: user.email || '-',
        mobileNumber: user.mobileNumber || '-',
        createdAt: user.createdAt ? new Date(user.createdAt).toLocaleString() : "",
        updatedAt: user.updatedAt ? new Date(user.updatedAt).toLocaleString() : "",
    }));

    // Excel Export logic
    const handleExport = async () => {
        setIsExporting(true);
        try {
            const token = localStorage.getItem("astroVani-admin-token");
            const params = new URLSearchParams();
            params.append('startDate', convertISTToUTCForAPI(appliedStartDate || todayDate, 'start'));
            params.append('endDate', convertISTToUTCForAPI(appliedEndDate || todayDate, 'end'));
            if (appliedSearchQuery) params.append(appliedSearchType, appliedSearchQuery);

            const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/user/export?${params.toString()}`, {
                headers: { Authorization: token }
            });
            const result = await response.json();
            const users = result?.data || result || [];

            const worksheetData = [
                ["Full Name", "Email", "Mobile Number", "Created At", "Updated At"],
                ...users.map(u => [u.fullName, u.email, u.mobileNumber, u.createdAt, u.updatedAt])
            ];
            const ws = XLSX.utils.aoa_to_sheet(worksheetData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Users");
            XLSX.writeFile(wb, "users_list.xlsx");
            toast.success("Excel exported!");
        } catch (e) { toast.error("Export failed"); }
        finally { setIsExporting(false); }
    };

    return (
        <>
            <div className="container-xxl">
                <PageHeader1 pagetitle={`Users List (${paginationInfo?.totalDocs || 0})`} />
                <div className="card modern-card mt-3">
                    <div className="card-body p-3">
                        {/* Filters Section */}
                        <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Search By</InputLabel>
                                <Select value={searchType} label="Search By" onChange={(e) => setSearchType(e.target.value)}>
                                    <MenuItem value="fullName">Full Name</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="mobileNumber">Mobile Number</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                placeholder="Search..."
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                                sx={{ width: 200 }}
                            />
                            <TextField label="Start Date" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <TextField label="End Date" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />

                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={loading || isApplyingFilters}
                            >
                                {isApplyingFilters ? "Applying..." : "Apply Filters"}
                            </Button>
                            <Button variant="outlined" onClick={handleResetFilter}>Reset</Button>

                            <Button variant="contained" color="success" startIcon={<BsDownload />} onClick={handleExport} disabled={isExporting}>
                                {isExporting ? "Exporting..." : "Excel"}
                            </Button>
                        </Box>

                        {/* Data Grid Section */}
                        <Box sx={{ width: '100%' }}>
                            <DataGrid
                                rows={currentGridRows}
                                columns={columns}
                                autoHeight
                                loading={loading}
                                paginationMode="server"
                                hideFooterPagination
                                disableRowSelectionOnClick
                                sx={{ borderRadius: '12px', overflow: 'hidden' }}
                            />
                            <TablePagination
                                component="div"
                                count={paginationInfo?.totalDocs || 0}
                                page={paginationModel.page}
                                onPageChange={handleChangePage}
                                rowsPerPage={paginationModel.pageSize}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                            />
                        </Box>
                    </div>
                </div>
            </div>
        </>
    );
}

export default UsersList;